import { useEffect, useMemo, useState } from 'react';
import { fetchList } from './api';

const moeda = (v) => `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => `${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const ConstrutorHome = () => {
  const [loading, setLoading] = useState(false);
  const [obras, setObras] = useState([]);
  const [alertas, setAlertas] = useState([]);
  const [capital, setCapital] = useState(null);
  const [saude, setSaude] = useState(null);
  const [motor, setMotor] = useState(null);
  const [precificacao, setPrecificacao] = useState([]);
  const [crescimento, setCrescimento] = useState(null);
  const [liquidez, setLiquidez] = useState(null);
  const [painelRisco, setPainelRisco] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await fetchList('/api/construtor/dashboard');
        setObras(Array.isArray(data?.obras) ? data.obras : []);
        setAlertas(Array.isArray(data?.alertas) ? data.alertas : []);
        setCapital(data?.capital_imobilizado || null);
        setSaude(data?.saude_negocio || null);
        setMotor(data?.motor_decisao || null);
        setPrecificacao(Array.isArray(data?.precificacao_inteligente) ? data.precificacao_inteligente : []);
        setCrescimento(data?.planejamento_crescimento || null);
        setLiquidez(data?.liquidez || null);
        setPainelRisco(data?.painel_risco || null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const resumo = useMemo(() => {
    return obras.reduce(
      (acc, obra) => {
        acc.custo += Number(obra.custo_total_realizado || 0);
        acc.receita += Number(obra.receita_realizada || 0);
        acc.capital += Number(obra.capital_investido_atual || 0);
        return acc;
      },
      { custo: 0, receita: 0, capital: 0 }
    );
  }, [obras]);

  return (
    <div className="animate-fade-in space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Construtor</h1>
        <p className="text-gray-600">Visao consolidada de custo real, margem real, capital investido e risco operacional.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Obras</p>
            <p className="text-2xl font-semibold text-gray-900">{obras.length}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Custo Realizado</p>
            <p className="text-2xl font-semibold text-gray-900">{moeda(resumo.custo)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Capital Investido</p>
            <p className="text-2xl font-semibold text-gray-900">{moeda(resumo.capital)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Score de Saude</p>
            <p className="text-2xl font-semibold text-gray-900">{Number(saude?.score || 0).toFixed(1)}</p>
            <p className="text-xs text-gray-500 capitalize">Risco {saude?.faixa_risco || '-'}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-header">Controle de Capital Imobilizado</div>
          <div className="card-body grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Terrenos</p>
              <p className="text-lg font-semibold">{moeda(capital?.terrenos)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Obras</p>
              <p className="text-lg font-semibold">{moeda(capital?.obras)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Estoque</p>
              <p className="text-lg font-semibold">{moeda(capital?.estoque)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Total Imobilizado</p>
              <p className="text-lg font-semibold">{moeda(capital?.total)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Tempo Medio de Retorno</p>
              <p className="text-lg font-semibold">{Number(capital?.tempo_medio_retorno_dias || 0).toFixed(0)} dias</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Giro de Capital</p>
              <p className="text-lg font-semibold">{Number(capital?.giro_capital || 0).toFixed(2)}x</p>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Indicador de Saude do Negocio</div>
          <div className="card-body grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-gray-500">Margem Media</p>
              <p className="text-lg font-semibold">{pct(saude?.margem_media_pct)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Prazo Medio de Obra</p>
              <p className="text-lg font-semibold">{Number(saude?.prazo_medio_obra_dias || 0).toFixed(0)} dias</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">% Estouro Orcamento</p>
              <p className="text-lg font-semibold">{pct(saude?.percentual_estouro_orcamento)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Liquidez</p>
              <p className="text-lg font-semibold">{Number(saude?.liquidez || 0).toFixed(2)}x</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Endividamento</p>
              <p className="text-lg font-semibold">{pct(saude?.endividamento_pct)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Painel de Risco</p>
              <p className="text-lg font-semibold capitalize">{painelRisco?.risco || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Alertas Inteligentes</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Severidade</th>
                  <th>Obra</th>
                  <th>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {alertas.map((a, idx) => (
                  <tr key={`${a.obra_id || 'global'}-${a.tipo}-${idx}`}>
                    <td>{a.tipo}</td>
                    <td className="capitalize">{a.severidade || '-'}</td>
                    <td>{a.obra_nome || '-'}</td>
                    <td>{a.mensagem}</td>
                  </tr>
                ))}
                {!loading && alertas.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-gray-500 py-6">Sem alertas no momento</td></tr>
                )}
                {loading && (
                  <tr><td colSpan={4} className="text-center text-gray-500 py-6">Carregando...</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-header">Motor de Decisao</div>
          <div className="card-body space-y-3">
            <p className="text-gray-800 font-medium">{motor?.recomendacao || '-'}</p>
            <div>
              <p className="text-xs text-gray-500">Fatores</p>
              <ul className="list-disc list-inside text-sm text-gray-700">
                {(motor?.fatores || []).map((fator, idx) => (
                  <li key={`${fator}-${idx}`}>{fator}</li>
                ))}
                {(motor?.fatores || []).length === 0 && <li>Sem fatores registrados</li>}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Caixa livre:</span> <span className="font-medium">{moeda(motor?.caixa_livre)}</span>
              </div>
              <div>
                <span className="text-gray-500">Obras ativas:</span> <span className="font-medium">{Number(motor?.obras_ativas || 0)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Liquidez e Caixa</div>
          <div className="card-body grid grid-cols-1 gap-2 text-sm">
            <div><span className="text-gray-500">Caixa disponivel:</span> <span className="font-medium">{moeda(liquidez?.caixa_disponivel)}</span></div>
            <div><span className="text-gray-500">Receitas previstas:</span> <span className="font-medium">{moeda(liquidez?.receitas_previstas)}</span></div>
            <div><span className="text-gray-500">Despesas previstas:</span> <span className="font-medium">{moeda(liquidez?.despesas_previstas)}</span></div>
            <div><span className="text-gray-500">Liquidez corrente:</span> <span className="font-medium">{Number(liquidez?.liquidez_corrente || 0).toFixed(2)}x</span></div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Precificacao Inteligente da Casa</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Obra</th>
                  <th>Custo Base</th>
                  <th>Margem Alvo</th>
                  <th>Preco Minimo Viavel</th>
                  <th>Preco Sugerido</th>
                  <th>Gap</th>
                </tr>
              </thead>
              <tbody>
                {precificacao.map((item) => (
                  <tr key={item.obra_id}>
                    <td>{item.obra_nome}</td>
                    <td>{moeda(item.custo_base)}</td>
                    <td>{pct(item.margem_alvo_pct)}</td>
                    <td>{moeda(item.preco_minimo_viavel)}</td>
                    <td>{moeda(item.preco_sugerido)}</td>
                    <td>{moeda(item.gap_para_minimo)}</td>
                  </tr>
                ))}
                {precificacao.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-6">Sem dados para precificacao</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Planejamento de Crescimento</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Cenario</th>
                  <th>Investimento</th>
                  <th>Caixa Pos Cenario</th>
                  <th>Lucro Estimado</th>
                </tr>
              </thead>
              <tbody>
                {(crescimento?.simulacoes || []).map((sim) => (
                  <tr key={sim.codigo}>
                    <td>{sim.titulo}</td>
                    <td>{moeda(sim.investimento_estimado)}</td>
                    <td>{moeda(sim.caixa_pos_cenario)}</td>
                    <td>{moeda(sim.lucro_estimado)}</td>
                  </tr>
                ))}
                {(crescimento?.simulacoes || []).length === 0 && <tr><td colSpan={4} className="text-center text-gray-500 py-6">Sem simulacoes disponiveis</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Resumo por obra</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Obra</th>
                  <th>Status</th>
                  <th>Custo Real</th>
                  <th>Receita</th>
                  <th>Capital Investido</th>
                  <th>Margem</th>
                  <th>Custo/m2</th>
                </tr>
              </thead>
              <tbody>
                {obras.map((obra) => (
                  <tr key={obra.id}>
                    <td>{obra.nome}</td>
                    <td>{obra.status}</td>
                    <td>{moeda(obra.custo_total_realizado)}</td>
                    <td>{moeda(obra.receita_realizada)}</td>
                    <td>{moeda(obra.capital_investido_atual)}</td>
                    <td>{Number.isFinite(Number(obra.margem_estimada_pct)) ? pct(obra.margem_estimada_pct) : '-'}</td>
                    <td>{obra.custo_por_m2 ? moeda(obra.custo_por_m2) : '-'}</td>
                  </tr>
                ))}
                {!loading && obras.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-6">Nenhuma obra cadastrada</td></tr>}
                {loading && <tr><td colSpan={7} className="text-center text-gray-500 py-6">Carregando...</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConstrutorHome;
